import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Plus,
  Heart,
  Share2,
  Image,
  FileText,
  X,
  MessageCircle,
  User,
  Loader2,
  File,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAllKnowledgePosts,
  getPostsByUser,
  createKnowledgePost,
  togglePostLike,
  uploadKnowledgeAttachment,
  KnowledgePost,
  KnowledgeAttachment,
} from "@/services/knowledgeService";

export default function KnowledgeSharingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.documentId || user?.id || user?.phone || "";
  const [posts, setPosts] = useState<KnowledgePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"all" | "my">("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newPost, setNewPost] = useState({
    content: "",
    attachments: [] as KnowledgeAttachment[],
  });

  useEffect(() => {
    loadPosts();
  }, [activeTab, user]);

  const loadPosts = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const data =
        activeTab === "my"
          ? await getPostsByUser(currentUserId)
          : await getAllKnowledgePosts();
      setPosts(data);
    } catch (error) {
      console.error("Error loading posts:", error);
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId: string) => {
    if (!user || !currentUserId) return;

    try {
      await togglePostLike(postId, currentUserId);

      // Optimistically update UI
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id === postId) {
            const isLiked = p.likedBy.includes(currentUserId);
            return {
              ...p,
              likes: isLiked ? p.likes - 1 : p.likes + 1,
              likedBy: isLiked
                ? p.likedBy.filter((id) => id !== currentUserId)
                : [...p.likedBy, currentUserId],
            };
          }
          return p;
        }),
      );
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Failed to update like");
    }
  };

  const handleShare = (post: KnowledgePost) => {
    const shareText = `📢 Knowledge Share from ${post.authorName}:\n\n${post.content}\n\nShared via DsaLens`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank");
    toast.success("Opening WhatsApp to share");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user || !currentUserId) return;

    try {
      setUploading(true);
      const uploadPromises = Array.from(files).map((file) =>
        uploadKnowledgeAttachment(file, currentUserId),
      );

      const uploadedAttachments = await Promise.all(uploadPromises);
      setNewPost((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedAttachments],
      }));

      toast.success(`${uploadedAttachments.length} file(s) uploaded`);
    } catch (error: any) {
      console.error("Error uploading files:", error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setNewPost((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((a) => a.id !== id),
    }));
  };

  const handleCreatePost = async () => {
    if (!user) return;

    if (!currentUserId) {
      toast.error("Unable to identify your account. Please sign in again.");
      return;
    }

    if (!newPost.content.trim()) {
      toast.error("Please write something to post");
      return;
    }

    try {
      setUploading(true);
      await createKnowledgePost(
        newPost.content,
        currentUserId,
        user.name,
        newPost.attachments,
      );

      toast.success("Post created successfully!");
      setCreateDialogOpen(false);
      setNewPost({ content: "", attachments: [] });

      // Reload posts
      await loadPosts();
    } catch (error: any) {
      console.error("Error creating post:", error);
      toast.error(`Failed to create post: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const getAttachmentIcon = (type: string) => {
    switch (type) {
      case "image":
        return <Image className="h-3 w-3" />;
      case "video":
        return <Video className="h-3 w-3" />;
      case "pdf":
        return <FileText className="h-3 w-3" />;
      default:
        return <File className="h-3 w-3" />;
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto pb-24 md:pb-8">
        <div className="flex items-center gap-3 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/tools")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeader
            title="Knowledge Sharing"
            subtitle="Share and learn from the team"
          />
        </div>

        <div className="space-y-4">
          {/* Tabs and Create Button */}
          <div className="flex items-center justify-between gap-4">
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as "all" | "my")}
            >
              <TabsList>
                <TabsTrigger value="all">All Posts</TabsTrigger>
                <TabsTrigger value="my">My Posts</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button onClick={() => setCreateDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Create Post</span>
            </Button>
          </div>

          {/* Posts List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="overflow-hidden">
                    <CardContent className="p-4">
                      {/* Author Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">
                            {post.authorName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(post.createdAt, "MMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                      </div>

                      {/* Content */}
                      <p className="text-sm whitespace-pre-wrap mb-3">
                        {post.content}
                      </p>

                      {/* Attachments */}
                      {post.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.attachments.map((att) => (
                            <a
                              key={att.id}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block"
                            >
                              <Badge
                                variant="secondary"
                                className="flex items-center gap-1 py-1.5 cursor-pointer hover:bg-secondary/80"
                              >
                                {getAttachmentIcon(att.type)}
                                <span className="max-w-[100px] truncate">
                                  {att.name}
                                </span>
                              </Badge>
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-4 pt-2 border-t">
                        <button
                          onClick={() => handleLike(post.id)}
                          className={`flex items-center gap-1.5 text-sm transition-colors ${
                            user && post.likedBy.includes(currentUserId)
                              ? "text-destructive"
                              : "text-muted-foreground hover:text-destructive"
                          }`}
                        >
                          <Heart
                            className={`h-4 w-4 ${
                              user && post.likedBy.includes(currentUserId)
                                ? "fill-current"
                                : ""
                            }`}
                          />
                          <span>{post.likes}</span>
                        </button>
                        <button
                          onClick={() => handleShare(post)}
                          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-success transition-colors"
                        >
                          <Share2 className="h-4 w-4" />
                          <span>Share</span>
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              {posts.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {activeTab === "my"
                        ? "You haven't posted anything yet"
                        : "No posts yet"}
                    </p>
                    <Button
                      variant="link"
                      onClick={() => setCreateDialogOpen(true)}
                      className="mt-2"
                    >
                      Create your first post
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Create Post Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Post</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>What would you like to share?</Label>
                <Textarea
                  value={newPost.content}
                  onChange={(e) =>
                    setNewPost((prev) => ({ ...prev, content: e.target.value }))
                  }
                  placeholder="Share your knowledge, tips, or updates..."
                  rows={5}
                  className="text-base"
                  disabled={uploading}
                />
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div className="flex flex-wrap gap-2">
                  {newPost.attachments.map((att) => (
                    <Badge
                      key={att.id}
                      variant="secondary"
                      className="flex items-center gap-1 py-1.5"
                    >
                      {getAttachmentIcon(att.type)}
                      <span className="max-w-[80px] truncate">{att.name}</span>
                      <button
                        onClick={() => handleRemoveAttachment(att.id)}
                        disabled={uploading}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*,video/*,.pdf,.doc,.docx,.pptx,.xlsx"
                    disabled={uploading}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    asChild
                    disabled={uploading}
                  >
                    <span>
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Files
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>

              <Button
                onClick={handleCreatePost}
                className="w-full"
                disabled={uploading || !newPost.content.trim()}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  "Post"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
